import { useEffect, type ReactNode } from 'react';
import type { RpLayout } from '@mlm/shared';
import { cn } from '../lib/cn';
import { RpRenderProvider, type RpRenderCtx } from './context';
import { RpSectionBand, RpSectionContent } from './section-renderer';
import { applyRpFontLinks, rpThemeVars } from './theme';

/**
 * THE renderer for a replicated landing page — used by the member app for the
 * live public page and by the admin app for the builder's preview. One
 * component, two hosts: the pre-builder editor kept its own copy for previewing
 * and it had already drifted from what visitors actually saw.
 *
 * The host supplies everything environment-shaped (image URLs, HTML
 * sanitization, the sponsor ref) through `ctx`, so this package stays free of
 * any API client or origin.
 */
export function RpPageRenderer({
  layout,
  ctx,
  className,
  header,
  footer,
}: {
  layout: RpLayout;
  ctx: RpRenderCtx;
  className?: string;
  /** Chrome above the content (the countdown banner). */
  header?: ReactNode;
  /** Chrome below the content (the auto-appended signup CTA). */
  footer?: ReactNode;
}) {
  const fontLinks = layout.theme?.fontLinks;
  useEffect(() => applyRpFontLinks(fontLinks), [fontLinks]);

  const sections = layout.sections.filter((s) => s.enabled);

  // ── version 0 — a page converted on read from pre-builder block rows ──────
  // Reproduces the historic page shell exactly: one narrow centered column with
  // uniform spacing, and each block rendered BARE (no band, no per-section
  // padding). An untouched legacy page must look the way it looked yesterday;
  // the builder's layout freedom starts the moment an admin saves it.
  if (layout.version === 0) {
    return (
      <div
        className={cn('min-h-screen bg-background', className)}
        style={rpThemeVars(layout.theme)}
      >
        {/* Header/footer sit INSIDE the column and participate in its
            `space-y-6`, exactly where the pre-builder page put them. */}
        <div className="mx-auto max-w-2xl px-4 py-10 space-y-6">
          {header}
          <RpRenderProvider value={ctx}>
            {sections.map((s) => (
              <RpSectionContent key={s.id} section={s} />
            ))}
          </RpRenderProvider>
          {footer}
        </div>
      </div>
    );
  }

  // ── version 1 — builder-authored. Sections own their own full-width bands. ─
  return (
    <div className={cn('min-h-screen bg-background', className)} style={rpThemeVars(layout.theme)}>
      {header ? <div className="mx-auto w-full max-w-4xl px-4 pt-6">{header}</div> : null}
      <RpRenderProvider value={ctx}>
        {sections.map((s) => (
          <RpSectionBand key={s.id} section={s} />
        ))}
      </RpRenderProvider>
      {footer ? (
        <div className="mx-auto w-full max-w-4xl px-4 py-10 text-center">{footer}</div>
      ) : null}
    </div>
  );
}
